import {
  Badge, Button, Card, EmptyState, ErrorState, LoadingState, Notice, Row, Stack,
  shiftIsoDate, useApi, useI18n,
  type CareEntryResponse, type DailyReportResponse,
} from '@daghfal/core';
import { useQuery } from '@tanstack/react-query';
import { useState, type ReactNode } from 'react';
import { useMyChild } from '../myChildren';

/**
 * One child's day — "يومي اليوم" (§4.5).
 *
 * The sections are in the order the client specified and every one of them renders, empty or not, because
 * a parent looking for "did she nap?" should find the heading and read "nothing recorded" rather than have
 * to work out whether the section is missing or the nap is.
 *
 * Arrival and departure are read from attendance, never from a copy held on the report: one fact, one
 * place (FR-ATT-01).
 */
export function Day({ childId }: { childId: number }) {
  const api = useApi();
  // The tenant's today, not the device's: a parent travelling is still looking at the nursery's day.
  const { t, tg, formatDate, formatTime, tn, today } = useI18n();
  const [date, setDate] = useState(today);

  const day = useQuery({
    queryKey: ['day', childId, date],
    queryFn: () => api.daily.forChild(childId, date),
  });

  // From the chooser's list: the first day of the child's history this guardian reads (Q28). Earlier days
  // answer 403, so they are not offered.
  const firstDay = useMyChild(childId)?.historyVisibleFrom;

  const shift = (days: number) => setDate((current) => shiftIsoDate(current, days));

  return (
    <Stack>
      <Row between>
        <Button variant="ghost" disabled={firstDay !== undefined && date <= firstDay} onClick={() => shift(-1)}>
          ‹ {t('day.previous')}
        </Button>
        <strong>{date === today() ? t('day.today') : formatDate(date)}</strong>
        {/* Never past today: there is no such thing as tomorrow's meals. */}
        <Button variant="ghost" disabled={date >= today()} onClick={() => shift(1)}>
          {t('day.next')} ›
        </Button>
      </Row>

      {day.isPending && <LoadingState />}
      {day.isError && <ErrorState error={day.error} onRetry={() => void day.refetch()} />}

      {day.data && <DayBody report={day.data} />}
    </Stack>
  );

  function DayBody({ report }: { report: DailyReportResponse }) {
    const empty =
      report.meals.length === 0 && report.drinks.length === 0 && report.activities.length === 0
      && report.sleeps.length === 0 && report.moods.length === 0 && report.diapers.length === 0
      && report.notes.length === 0;

    return (
      <Stack>
        <Card title={t('day.section.attendance')}>
          <Stack tight>
            {report.attendance.checkInAt
              ? <span>{t('day.arrived', { time: formatTime(report.attendance.checkInAt) })}</span>
              : <span className="dg-muted">{t('attendance.expected')}</span>}
            {report.attendance.checkOutAt && (
              <span>{t('day.left', { time: formatTime(report.attendance.checkOutAt) })}</span>
            )}
          </Stack>
        </Card>

        {/* FR-DAY-02: a day with nothing in it yet is a friendly empty state, never an error. */}
        {empty && (
          <EmptyState icon="🌱" title={t('day.empty')} />
        )}

        <Section title={t('day.section.meals')} entries={report.meals} render={(entry) => (
          <Stack tight>
            <Row>
              <strong>{entry.mealTypeName ?? t('care.kind.Meal')}</strong>
              {entry.amountEaten && <Badge tone="info">{t(`care.amount.${entry.amountEaten}`)}</Badge>}
            </Row>
            {entry.foodItems && entry.foodItems.length > 0 && (
              <span>{entry.foodItems.map((food) => food.name).join('، ')}</span>
            )}
          </Stack>
        )} />

        <Section title={t('day.section.drinks')} entries={report.drinks} render={(entry) => (
          <span>{entry.catalogItemIcon} {entry.catalogItemName}</span>
        )} />

        <Section title={t('day.section.activities')} entries={report.activities} render={(entry) => (
          <span>{entry.catalogItemIcon} {entry.catalogItemName}</span>
        )} />

        <Section title={t('day.section.sleep')} entries={report.sleeps} render={(entry) => (
          // FR-DAY-05: a nap with no end is in progress, and says so, rather than showing a blank.
          entry.endAt
            ? <span>{t('day.napRange', {
                from: formatTime(entry.startAt),
                to: formatTime(entry.endAt),
              })}</span>
            : <strong>{tg('day.sleeping', null, { time: formatTime(entry.startAt) })}</strong>
        )} />

        <Section title={t('day.section.mood')} entries={report.moods} render={(entry) => (
          <span>{entry.catalogItemIcon} {entry.catalogItemName}</span>
        )} />

        {/* Hidden entirely for the older rooms, where the section would be inappropriate rather than
            merely empty (FR-DAY-08). */}
        {report.diaperLogVisible && (
          <Section title={t('day.section.diaper')} entries={report.diapers} render={(entry) => (
            <Row>
              <span>{entry.catalogItemIcon} {entry.catalogItemName}</span>
              {entry.creamApplied && <Badge tone="neutral">{t('day.creamApplied')}</Badge>}
            </Row>
          )} />
        )}

        <Section title={t('day.section.notes')} entries={report.notes} render={(entry) => (
          <span>{entry.body}</span>
        )} />

        {report.photoCount > 0 && (
          <Notice tone="info" icon="📸">
            {t('day.photoCount', { count: tn('photos.count', report.photoCount) })}
          </Notice>
        )}
      </Stack>
    );
  }

  /** One section of the day. Renders its heading even when there is nothing under it. */
  function Section({ title, entries, render }: {
    title: string;
    entries: CareEntryResponse[];
    render: (entry: CareEntryResponse) => ReactNode;
  }) {
    return (
      <Card title={title}>
        {entries.length === 0
          ? <p className="dg-muted">{t('state.empty')}</p>
          : (
            <Stack>
              {entries.map((entry) => (
                <div key={`${entry.kind}-${entry.id}`} className="dg-timeline-entry">
                  <span className="dg-timeline-entry__time">{formatTime(entry.occurredAt)}</span>
                  <span className="dg-timeline-entry__body">
                    {render(entry)}
                    {entry.note && <p className="dg-muted">{entry.note}</p>}
                    {/* FR-DAY-03 asks for the recording staff member's name against every entry. */}
                    {entry.recordedByName && (
                      <p className="dg-muted dg-text-sm">
                        {t('day.recordedBy', { name: entry.recordedByName })}
                      </p>
                    )}
                  </span>
                </div>
              ))}
            </Stack>
          )}
      </Card>
    );
  }
}
